import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  CellCoord,
  WarehouseResponse,
  WarehouseService,
} from '@nebalkon-workspace/storage-api';
import { CabinetButtonComponent } from '@nebalkon-workspace/cabinet-client';
import { NotButton, NotInput, NotSystemNotificationService } from '@nebalkon-workspace/ui-kit';
import {
  formatWarehouseCellLabel,
  initialCellsKey,
  shouldSyncInitialCells,
  warehouseCellKey,
} from './staff-warehouse-cell-picker.util';

@Component({
  selector: 'lib-staff-warehouse-cell-picker',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    FormsModule,
    NotInput,
    CabinetButtonComponent,
    NotButton,
  ],
  templateUrl: './staff-warehouse-cell-picker.component.html',
  styleUrl: './staff-warehouse-cell-picker.component.scss',
})
export class StaffWarehouseCellPickerComponent {
  readonly warehouseId = input.required<string>();
  readonly initialCells = input<CellCoord[]>([]);
  readonly submitLabelKey = input('admin.warehouses.placement.submit');
  readonly submitting = input(false);
  readonly disabled = input(false);
  readonly showCancel = input(false);

  readonly submitCells = output<CellCoord[]>();
  readonly cancelled = output<void>();

  readonly #warehouseService = inject(WarehouseService);
  readonly #translate = inject(TranslateService);
  readonly #notification = inject(NotSystemNotificationService);

  protected readonly warehouse = signal<WarehouseResponse | null>(null);
  protected readonly loadingWarehouse = signal(true);
  protected readonly warehouseError = signal<string | null>(null);

  protected readonly rowNumber = signal<number | null>(null);
  protected readonly floor = signal<number | null>(null);
  protected readonly colNumber = signal<number | null>(null);
  protected readonly selectedCells = signal<CellCoord[]>([]);

  protected readonly rowOptions = computed(() =>
    [...(this.warehouse()?.rows ?? [])].sort(
      (left, right) => left.row_number - right.row_number,
    ),
  );

  protected readonly hasWarehouseGrid = computed(
    () => this.rowOptions().length > 0,
  );

  protected readonly floorOptions = computed(() => {
    const rowNumber = this.rowNumber();
    if (rowNumber === null) {
      return [];
    }
    const row = this.rowOptions().find((entry) => entry.row_number === rowNumber);
    if (!row) {
      return [];
    }
    return Array.from({ length: row.floors }, (_, index) => index + 1);
  });

  protected readonly columnOptions = computed(() => {
    const rowNumber = this.rowNumber();
    if (rowNumber === null) {
      return [];
    }
    const row = this.rowOptions().find((entry) => entry.row_number === rowNumber);
    if (!row) {
      return [];
    }
    return Array.from({ length: row.columns }, (_, index) => index + 1);
  });

  protected readonly rowDropdownOptions = computed(() =>
    this.rowOptions().map((row) => ({
      id: row.row_number,
      label: String(row.row_number),
    })),
  );

  protected readonly floorDropdownOptions = computed(() =>
    this.floorOptions().map((value) => ({ id: value, label: String(value) })),
  );

  protected readonly columnDropdownOptions = computed(() =>
    this.columnOptions().map((value) => ({ id: value, label: String(value) })),
  );

  protected readonly canAddCell = computed(() => {
    const row = this.rowNumber();
    const floorValue = this.floor();
    const column = this.colNumber();
    if (row === null || floorValue === null || column === null) {
      return false;
    }
    const config = this.rowOptions().find((entry) => entry.row_number === row);
    if (
      !config ||
      floorValue < 1 ||
      floorValue > config.floors ||
      column < 1 ||
      column > config.columns
    ) {
      return false;
    }
    const key = warehouseCellKey({
      row_number: row,
      floor: floorValue,
      col_number: column,
    });
    return !this.selectedCells().some(
      (cell) => warehouseCellKey(cell) === key,
    );
  });

  protected readonly selectedCellLabels = computed(() =>
    this.selectedCells().map((cell) => ({
      key: warehouseCellKey(cell),
      label: formatWarehouseCellLabel(cell),
    })),
  );

  protected readonly canSubmit = computed(
    () =>
      !this.disabled() &&
      !this.submitting() &&
      !this.loadingWarehouse() &&
      !this.warehouseError() &&
      this.hasWarehouseGrid() &&
      this.selectedCells().length > 0,
  );

  #syncedInitialCellsKey = '';

  constructor() {
    effect((onCleanup) => {
      const warehouseId = this.warehouseId();
      if (!warehouseId) {
        this.warehouse.set(null);
        this.loadingWarehouse.set(false);
        this.warehouseError.set(
          this.#translate.instant('admin.warehouses.placement.loadWarehouseError'),
        );
        return;
      }

      this.loadingWarehouse.set(true);
      this.warehouseError.set(null);
      let cancelled = false;
      onCleanup(() => {
        cancelled = true;
      });

      this.#warehouseService.getWarehouse(warehouseId).subscribe({
        next: (warehouse) => {
          if (cancelled) {
            return;
          }
          this.warehouse.set(warehouse);
          this.loadingWarehouse.set(false);
        },
        error: () => {
          if (cancelled) {
            return;
          }
          this.warehouse.set(null);
          this.loadingWarehouse.set(false);
          this.warehouseError.set(
            this.#translate.instant('admin.warehouses.placement.loadWarehouseError'),
          );
        },
      });
    });

    effect(() => {
      const incomingKey = initialCellsKey(this.initialCells());
      if (!shouldSyncInitialCells(this.#syncedInitialCellsKey, incomingKey)) {
        return;
      }
      this.#syncedInitialCellsKey = incomingKey;
      this.selectedCells.set([...this.initialCells()]);
      this.rowNumber.set(null);
      this.floor.set(null);
      this.colNumber.set(null);
    });
  }

  protected onRowModelChange(value: number | string | null): void {
    if (value === '' || value === null || value === undefined) {
      this.rowNumber.set(null);
      this.floor.set(null);
      this.colNumber.set(null);
      return;
    }
    const parsed = typeof value === 'number' ? value : Number(value);
    if (Number.isNaN(parsed)) {
      return;
    }
    this.rowNumber.set(parsed);
    this.floor.set(null);
    this.colNumber.set(null);
  }

  protected onFloorModelChange(value: number | string | null): void {
    if (value === '' || value === null || value === undefined) {
      this.floor.set(null);
      return;
    }
    const parsed = typeof value === 'number' ? value : Number(value);
    this.floor.set(Number.isNaN(parsed) ? null : parsed);
  }

  protected onColumnModelChange(value: number | string | null): void {
    if (value === '' || value === null || value === undefined) {
      this.colNumber.set(null);
      return;
    }
    const parsed = typeof value === 'number' ? value : Number(value);
    this.colNumber.set(Number.isNaN(parsed) ? null : parsed);
  }

  protected addCurrentCell(): void {
    const row = this.rowNumber();
    const floorValue = this.floor();
    const column = this.colNumber();
    if (
      row === null ||
      floorValue === null ||
      column === null ||
      !this.canAddCell()
    ) {
      this.#toastError('admin.warehouses.placement.validation');
      return;
    }

    this.selectedCells.update((cells) => [
      ...cells,
      {
        row_number: row,
        floor: floorValue,
        col_number: column,
      },
    ]);
    this.colNumber.set(null);
  }

  protected removeCell(key: string): void {
    this.selectedCells.update((cells) =>
      cells.filter((cell) => warehouseCellKey(cell) !== key),
    );
  }

  protected onSubmit(): void {
    if (!this.hasWarehouseGrid()) {
      this.#toastError('admin.warehouses.placement.noGridConfig');
      return;
    }
    if (this.selectedCells().length === 0) {
      this.#toastError('admin.warehouses.placement.needsCells');
      return;
    }
    this.submitCells.emit(this.selectedCells());
  }

  protected onCancel(): void {
    this.cancelled.emit();
  }

  #toastError(key: string): void {
    this.#notification.addToQueue({
      id: Date.now().toString(),
      type: 'error',
      text: [this.#translate.instant(key)],
    });
  }
}
